import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useLocation, useNavigate } from "react-router-dom";
import StyledDataGrid from "../../../components/StyledDataGrid";
import {
  fetchEmailTemplateSpecs,
  fetchEmailTemplates,
} from "../../../utils/api";
import {
  LANGUAGE_OPTIONS,
  SCOPE_OPTIONS,
  formatLanguage,
  formatSpecCode,
  getErrorMessage,
  normalizeSpecs,
  normalizeTemplates,
  templateIdentity,
} from "./emailFormatters";
import { BIOBANK_SUB_ROUTES } from "../../../constants/routes";

const defaultTemplateFilters = { scope: "FOLLOW_UP", language: "en" };
const placeholderColor = "#9e9e9e";

const EmailTemplatesTab = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [filters, setFilters] = useState(defaultTemplateFilters);
  const [specs, setSpecs] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [specResponse, templateResponse] = await Promise.all([
        fetchEmailTemplateSpecs(filters.scope ? { scope: filters.scope } : {}),
        fetchEmailTemplates(filters),
      ]);
      setSpecs(normalizeSpecs(specResponse));
      setRows(normalizeTemplates(templateResponse));
    } catch (err) {
      console.error("Failed to load email templates:", err);
      setRows([]);
      setSpecs([]);
      setError(getErrorMessage(err, "Failed to load email templates."));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (location.state?.refreshTemplates) {
      loadTemplates();
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [loadTemplates, location.pathname, location.search, location.state, navigate]);

  const update = (field) => (event) => {
    setFilters((current) => ({
      ...current,
      [field]: event.target.value,
      ...(field === "scope" ? { specCode: "" } : {}),
    }));
  };

  const specOptions = useMemo(() => {
    const seen = new Set();
    return specs.filter((spec) => {
      if (!spec?.specCode || seen.has(spec.specCode)) return false;
      seen.add(spec.specCode);
      return true;
    });
  }, [specs]);

  const handleClear = () => {
    setFilters(defaultTemplateFilters);
  };

  const handleEdit = useCallback((template) => {
    navigate(
      BIOBANK_SUB_ROUTES.EMAIL_CENTER_TEMPLATE_PROFILE(
        encodeURIComponent(template.specCode),
        encodeURIComponent(template.language),
      ),
      { state: { template, specs } },
    );
  }, [navigate, specs]);

  const columns = useMemo(() => [
    {
      field: "name",
      headerName: "Name",
      minWidth: 240,
      flex: 1,
      renderCell: (params) => (
        <Tooltip
          title={params.row.description || ""}
          arrow
          slotProps={{
            tooltip: {
              sx: {
                color: "#fff",
                bgcolor: "#333",
              },
            },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>
            {params.value || "N/A"}
          </Typography>
        </Tooltip>
      ),
    },
    { field: "scope", headerName: "Scope", minWidth: 120, flex: 0.45 },
    {
      field: "language",
      headerName: "Language",
      minWidth: 115,
      flex: 0.4,
      valueFormatter: (value) => formatLanguage(value),
    },
    {
      field: "subjectTemplate",
      headerName: "Subject",
      minWidth: 420,
      flex: 1.8,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            py: 1,
            whiteSpace: "normal",
            overflowWrap: "anywhere",
            lineHeight: 1.45,
          }}
        >
          {params.value || "N/A"}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Action",
      minWidth: 90,
      flex: 0.25,
      sortable: false,
      filterable: false,
      align: "center",
      renderCell: (params) => (
        <Tooltip title="Edit template" arrow>
          <IconButton size="small" onClick={() => handleEdit(params.row)} aria-label="Edit template" sx={{ borderRadius: "10px" }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [handleEdit]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            displayEmpty
            value={filters.scope || ""}
            onChange={update("scope")}
            renderValue={(selected) => {
              if (!selected) return <span style={{ color: placeholderColor }}>Scope</span>;
              return selected;
            }}
          >
            <MenuItem value=""><em>CLEAR SELECTION</em></MenuItem>
            {SCOPE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <Select
            displayEmpty
            value={filters.specCode || ""}
            onChange={update("specCode")}
            renderValue={(selected) => {
              if (!selected) return <span style={{ color: placeholderColor }}>Template name</span>;
              return formatSpecCode(selected);
            }}
          >
            <MenuItem value=""><em>CLEAR SELECTION</em></MenuItem>
            {specOptions.map((spec) => (
              <MenuItem key={spec.specCode} value={spec.specCode}>
                {formatSpecCode(spec.specCode)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 145 }}>
          <Select
            displayEmpty
            value={filters.language || ""}
            onChange={update("language")}
            renderValue={(selected) => {
              if (!selected) return <span style={{ color: placeholderColor }}>Language</span>;
              return formatLanguage(selected);
            }}
          >
            <MenuItem value=""><em>CLEAR SELECTION</em></MenuItem>
            {LANGUAGE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>{formatLanguage(option)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" onClick={handleClear}>
          Clear
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ flex: 1, minHeight: 0 }} data-tour-id="tour-email-templates-table">
        <StyledDataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.templateId || templateIdentity(row)}
          disableRowSelectionOnClick
          getRowHeight={() => "auto"}
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ p: 3, textAlign: "center" }}>
                No templates found.
              </Box>
            ),
          }}
        />
      </Box>
    </Box>
  );
};

export default EmailTemplatesTab;
